import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import { type CanaryPromptRunResult, useCanaryPromptRunResults } from '@/api/aiVisibility';
import { Button } from '@/components/buttons';
import { Empty } from '@/components/Empty';
import WidgetError from '@/components/errors/WidgetError';
import { SearchInput } from '@/components/Form/inputs/SearchInput';
import Markdown from '@/components/page/ai/Markdown';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/Sheet';
import { Skeleton } from '@/components/Skeleton';
import { Text, Title } from '@/components/typography';
import { Badge } from '@/components/typography/Badge';
import { cn } from '@/lib/utils';

import {
  getRecommendationCategoryColor,
  getRecommendationCategoryLabel,
} from './RecommendationTags';

type Prompt = {
  id: string;
  text: string;
  type: string | null;
  results: CanaryPromptRunResult[];
};

const getCategoryLabel = (type: string | null) => {
  if (!type) return t`Uncategorized`;
  return getRecommendationCategoryLabel(type);
};

const getCategoryColor = (type: string | null) =>
  type ? getRecommendationCategoryColor(type) : 'bg-gray-700 text-white';

const getModelHeading = (provider: string | null, model: string | null) => {
  const name = provider ?? model;
  if (!name) return t`AI model`;
  const normalized = name.toLowerCase().replace(/[_\s-]/g, '');
  if (normalized === 'openai') return t`OpenAI`;
  if (normalized === 'anthropic') return t`Anthropic`;
  if (normalized === 'gemini') return t`Gemini`;
  if (normalized === 'chatgpt') return t`ChatGPT`;
  return name.replaceAll('_', ' ').replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
};

const AIVisibilityPrompts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const promptId = searchParams.get('promptId');
  const { promptResults, isLoading, error } = useCanaryPromptRunResults(
    promptId ? [promptId] : undefined,
  );
  const [category, setCategory] = useState<string | null>();
  const [search, setSearch] = useState('');
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);

  const promptsById = new Map<string, Prompt>();
  for (const result of promptResults ?? []) {
    if (promptId && result.promptId !== promptId) continue;
    const prompt = promptsById.get(result.promptId);
    if (prompt) {
      prompt.results.push(result);
    } else {
      promptsById.set(result.promptId, {
        id: result.promptId,
        text: result.promptText,
        type: result.promptType,
        results: [result],
      });
    }
  }
  const prompts = Array.from(promptsById.values());
  const categories = Array.from(new Set(prompts.map((prompt) => prompt.type))).sort((a, b) =>
    getCategoryLabel(a).localeCompare(getCategoryLabel(b)),
  );
  const normalizedSearch = search.trim().toLowerCase();
  const visiblePrompts = prompts.filter(
    (prompt) =>
      (promptId !== null || category === undefined || prompt.type === category) &&
      (prompt.text.toLowerCase().includes(normalizedSearch) ||
        getCategoryLabel(prompt.type).toLowerCase().includes(normalizedSearch)),
  );
  const selectedPrompt = promptsById.get(selectedPromptId ?? '') ?? null;

  const handleShowAll = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('promptId');
    setCategory(undefined);
    setSearchParams(nextSearchParams);
  };

  const handleCategoryChange = (type?: string | null) => {
    if (promptId) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('promptId');
      setSearchParams(nextSearchParams);
    }
    setCategory(type);
  };

  return (
    <div className="flex flex-col gap-5 pt-6">
      <div>
        {!isLoading && !error && (
          <Title level={2} variant="label">
            {plural(prompts.length, { one: '# prompt', other: '# prompts' })}
          </Title>
        )}
        <Text variant="label" weight="regular" className="mt-1 block">
          <Trans>Search and open a prompt to review answers from each AI model.</Trans>
        </Text>
      </div>

      {!isLoading && !error && (
        <div className="flex flex-wrap items-center gap-2" aria-label={t`Prompt categories`}>
          <button
            type="button"
            aria-pressed={category === undefined}
            className={cn(
              'cursor-pointer rounded-full bg-gray-900 px-3 py-1 text-xs text-white focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:outline-none',
              category === undefined && 'ring-2 ring-green-400 ring-offset-2',
            )}
            onClick={() => handleCategoryChange()}
          >
            <Trans>ALL</Trans> ({prompts.length})
          </button>
          {categories.map((promptType) => (
            <button
              key={promptType ?? ''}
              type="button"
              aria-pressed={category === promptType}
              className={cn(
                'cursor-pointer rounded-full px-3 py-1 text-xs focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:outline-none',
                getCategoryColor(promptType),
                category === promptType && 'ring-2 ring-green-400 ring-offset-2',
              )}
              onClick={() => handleCategoryChange(promptType)}
            >
              {getCategoryLabel(promptType)} (
              {prompts.filter((prompt) => prompt.type === promptType).length})
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          aria-label={t`Search prompts`}
          className="w-full max-w-116"
          placeholder={t`Search`}
          value={search}
          debounce={false}
          onValueChange={setSearch}
        />
        {promptId && (
          <Button size="small" variant="ghost" onClick={handleShowAll}>
            <Trans>Show all prompts</Trans>
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="grid gap-4 xl:grid-cols-2" aria-busy="true">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-22 w-full rounded-2xl" />
          ))}
        </div>
      )}
      {!isLoading && !!error && <WidgetError message={<Trans>Unable to load prompts.</Trans>} />}
      {!isLoading && !error && !visiblePrompts.length && (
        <Empty>
          <Trans>No prompts found.</Trans>
        </Empty>
      )}
      {!isLoading && !error && !!visiblePrompts.length && (
        <div className="grid gap-4 xl:grid-cols-2">
          {visiblePrompts.map((prompt) => (
            <button
              key={prompt.id}
              type="button"
              className="flex min-h-22 w-full min-w-0 cursor-pointer flex-col items-start gap-2 rounded-2xl border-3 border-gray-300 bg-white px-4 py-3 text-left transition-colors hover:border-gray-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500"
              onClick={() => setSelectedPromptId(prompt.id)}
            >
              <Text variant="label" className="wrap-break-word">
                {prompt.text}
              </Text>
              <Badge
                size="small"
                color="default"
                variant="solid"
                showTooltip={false}
                className={getCategoryColor(prompt.type)}
              >
                {getCategoryLabel(prompt.type)}
              </Badge>
            </button>
          ))}
        </div>
      )}

      <Sheet
        open={selectedPrompt != null}
        onOpenChange={(open) => {
          if (!open) setSelectedPromptId(null);
        }}
      >
        <SheetContent
          side="right"
          className="gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-220"
        >
          <SheetHeader className="pr-14">
            <SheetTitle
              render={(props) => (
                <Title
                  {...props}
                  level={2}
                  variant="kpi"
                  color="black"
                  weight="regular"
                  className=""
                />
              )}
            >
              {selectedPrompt?.text}
            </SheetTitle>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-7 overflow-y-auto p-6">
            {selectedPrompt?.results.map((result) => (
              <section key={result.id} className="flex flex-col gap-3">
                <div>
                  <Title level={3} variant="label">
                    {getModelHeading(result.provider, result.model)}
                  </Title>
                  {result.provider && result.model && (
                    <p className="text-xs text-gray-600">{result.model}</p>
                  )}
                </div>
                <div className="rounded-xl border border-dashed border-green-400 bg-green-100 p-4">
                  {result.responseText ? (
                    <Markdown variant="label">{result.responseText}</Markdown>
                  ) : (
                    <Text variant="label" weight="regular">
                      <Trans>No answer available.</Trans>
                    </Text>
                  )}
                </div>
              </section>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AIVisibilityPrompts;
